import { COLORS } from './colors';

// plan-gen1.md Experiment 3: background depth. A vignette (radial gradient,
// bg -> darker at corners) plus a very low-alpha scanline pattern, rendered
// ONCE into an offscreen canvas keyed by pixel size, then drawImage'd per
// frame -- not recomputed at 60fps. Painted INSIDE the game canvas, behind
// the maze (renderMaze calls this in place of a flat fillRect), so it never
// composites over any wall/pellet/entity core pixel. COLORS.bg is used here
// only as a compositing/contrast base, never as a tolerance-6 pixel
// assertion target (verified against scripts/shoot.mjs).

interface BgCache {
  key: string;
  canvas: HTMLCanvasElement;
}

let cache: BgCache | null = null;

function buildBackground(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const cx = width / 2;
  const cy = height / 2;
  const outerR = Math.sqrt(cx * cx + cy * cy);
  const grad = ctx.createRadialGradient(cx, cy, outerR * 0.35, cx, cy, outerR);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.4)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = 'rgba(255,255,255,0.025)';
  for (let y = 0; y < height; y += 3) {
    ctx.fillRect(0, y, width, 1);
  }

  return canvas;
}

/** Draws the cached vignette+scanline background into `ctx`, rebuilding the
 * offscreen layer only when the pixel size changes. */
export function renderBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const key = `${width}x${height}`;
  if (!cache || cache.key !== key) {
    cache = { key, canvas: buildBackground(width, height) };
  }
  ctx.drawImage(cache.canvas, 0, 0);
}
